import React, { useState } from "react";
import { X, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

const ResumeMatchingModal = ({ open, onClose, onSubmit, jobTitle }) => {
  const { t } = useTranslation();
  const [topN, setTopN] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit(topN);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform transition-all animate-in zoom-in-95 duration-200 overflow-hidden">
        {/* Header with gradient */}
        <div className="relative p-6 text-white" style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)" }}>
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
                <Users size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">
                  {t("jobDescription.resumeMatching.title")}
                </h3>
                <p className="text-sm text-blue-50 font-medium">{jobTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 text-white hover:bg-white/20 rounded-xl transition-all duration-200 disabled:opacity-50"
              disabled={isSubmitting}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                {t("jobDescription.resumeMatching.label")}
              </label>
              <select
                value={topN}
                onChange={(e) => setTopN(Number(e.target.value))}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm hover:shadow-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <option value={5}>{t("jobDescription.resumeMatching.option", { count: 5 })}</option>
                <option value={10}>{t("jobDescription.resumeMatching.option", { count: 10 })}</option>
                <option value={15}>{t("jobDescription.resumeMatching.option", { count: 15 })}</option>
                <option value={20}>{t("jobDescription.resumeMatching.option", { count: 20 })}</option>
                <option value={25}>{t("jobDescription.resumeMatching.option", { count: 25 })}</option>
                <option value={30}>{t("jobDescription.resumeMatching.option", { count: 30 })}</option>
                <option value={50}>{t("jobDescription.resumeMatching.option", { count: 50 })}</option>
              </select>
              <p className="mt-3 text-sm text-gray-600">
                {t("jobDescription.resumeMatching.placeholder")}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-8 pt-6 border-t-2 border-gray-200">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("jobDescription.resumeMatching.processing")}
                </span>
              ) : (
                t("jobDescription.resumeMatching.submit")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeMatchingModal;